/* PrefetchLink — a React Router <Link> that prefetches the destination's
 * JS chunk on hover, focus, or touchstart. Drop-in replacement for <Link>.
 *
 * Why: routes are lazy-loaded so the first navigation to a page like
 * /investment-board can stall for ~300-700ms while the chunk downloads. By
 * firing the import on hover (when intent is detected but click hasn't
 * happened yet), the chunk is ready by the time the user actually clicks.
 */
import { forwardRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { prefetchRoute } from '../utils/routePrefetch';

const PrefetchLink = forwardRef(({ to, onMouseEnter, onFocus, onTouchStart, ...rest }, ref) => {
  const target = typeof to === 'string' ? to : (to?.pathname || '');

  const prefetch = useCallback(() => {
    prefetchRoute(target);
  }, [target]);

  const handleMouseEnter = useCallback((e) => {
    prefetch();
    if (onMouseEnter) onMouseEnter(e);
  }, [prefetch, onMouseEnter]);

  const handleFocus = useCallback((e) => {
    prefetch();
    if (onFocus) onFocus(e);
  }, [prefetch, onFocus]);

  const handleTouchStart = useCallback((e) => {
    prefetch();
    if (onTouchStart) onTouchStart(e);
  }, [prefetch, onTouchStart]);

  return (
    <Link
      ref={ref}
      to={to}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onTouchStart={handleTouchStart}
      {...rest}
    />
  );
});

PrefetchLink.displayName = 'PrefetchLink';
export default PrefetchLink;
